import { motion } from 'framer-motion';
import { TrendingUp, Sparkles, Users, Wine, DollarSign, ArrowRight, Lock, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { apiClient } from '@/services/api/apiClient';

interface CustomerInsightsCardProps {
  plan: string;
  hasAccess: boolean;
}

interface InsightsSummary {
  topCombo?: {
    items: string[];
    totalPrice: number;
    frequency: number;
  };
  bestPriceRange?: {
    range: string;
    percentage: number;
    orders: number;
  };
  topDrinkPairing?: {
    food: string;
    drink: string;
    additionalRevenue: number;
  };
  weeklyOrders?: number;
  dataUpdated?: string;
  dateRange?: {
    start: string;
    end: string;
  };
}

const CustomerInsightsCard = ({ plan, hasAccess }: CustomerInsightsCardProps) => {
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState<InsightsSummary>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (hasAccess) {
      fetchInsightsSummary();
    }
  }, [hasAccess]);

  const fetchInsightsSummary = async () => {
    try {
      setLoading(true);
      // Fetch multiple endpoints in parallel for summary
      const [mealJourneys, priceData, drinkPairings] = await Promise.all([
        apiClient.get('/customer-insights/meal-journeys?days=7').catch(() => null),
        apiClient.get('/customer-insights/price-distribution?days=7').catch(() => null),
        apiClient.get('/customer-insights/drink-pairings?days=7').catch(() => null)
      ]);

      const summary: InsightsSummary = {};

      // Extract date range from any of the responses (they should all have the same range)
      const dateRangeSource = mealJourneys?.data || priceData?.data || drinkPairings?.data;
      if (dateRangeSource && dateRangeSource.week_starting && dateRangeSource.week_ending) {
        summary.dateRange = {
          start: dateRangeSource.week_starting,
          end: dateRangeSource.week_ending
        };
      }

      // Extract top combo from meal journeys
      if (mealJourneys?.data?.journeys?.length > 0) {
        const topJourney = mealJourneys.data.journeys[0];
        // Backend returns item1, item2, item3 structure
        if (topJourney.item1 && topJourney.item2) {
          const items = [
            topJourney.item1?.name,
            topJourney.item2?.name,
            topJourney.item3?.name
          ].filter(Boolean);

          summary.topCombo = {
            items: items,
            totalPrice: topJourney.total_value || 0,
            frequency: topJourney.frequency || 0
          };
        }
      }

      // Extract best price range
      if (priceData?.data?.distribution?.length > 0) {
        const bestRange = priceData.data.distribution
          .sort((a: any, b: any) => b.percentage - a.percentage)[0];
        // Backend returns 'label' and 'order_count', not 'range' and 'orders'
        if (bestRange && bestRange.label) {
          summary.bestPriceRange = {
            range: bestRange.label,
            percentage: bestRange.percentage || 0,
            orders: bestRange.order_count || 0
          };
        }
      }

      // Extract top drink pairing
      if (drinkPairings?.data?.pairings?.length > 0) {
        const topPairing = drinkPairings.data.pairings[0];
        // Backend returns drink_name and food_name, not drink_item and food_item
        if (topPairing && topPairing.food_name && topPairing.drink_name) {
          summary.topDrinkPairing = {
            food: topPairing.food_name,
            drink: topPairing.drink_name,
            additionalRevenue: topPairing.revenue_per_order || topPairing.drink_price || 0
          };
        }
      }

      // Get total weekly orders
      summary.weeklyOrders = mealJourneys?.data?.total_orders || 0;
      summary.dataUpdated = new Date().toLocaleDateString();

      // Always set summary data - let the UI handle empty states
      setSummaryData(summary);
    } catch (error) {
      console.error('Failed to fetch insights summary:', error);
      // Set empty state on error
      setSummaryData({});
    } finally {
      setLoading(false);
    }
  };

  // Format price
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Create dynamic insights based on real data
  const insights = [
    {
      icon: TrendingUp,
      label: 'Top Combo',
      value: loading ? 'Loading...' :
             summaryData.topCombo && summaryData.topCombo.items?.length > 0 ?
             `${summaryData.topCombo.items.slice(0, 2).join(' → ')}` :
             summaryData.dateRange ? 'No items yet - Engage X1! appears after your first week' : 'No data yet',
      subValue: summaryData.topCombo && summaryData.topCombo.frequency > 0 ?
                `${formatPrice(summaryData.topCombo.totalPrice / summaryData.topCombo.frequency)} avg` : '',
      color: 'text-emerald-400'
    },
    {
      icon: Wine,
      label: 'Best Pairing',
      value: loading ? 'Loading...' :
             summaryData.topDrinkPairing && summaryData.topDrinkPairing.drink && summaryData.topDrinkPairing.food ?
             `${summaryData.topDrinkPairing.drink} + ${summaryData.topDrinkPairing.food}` :
             summaryData.dateRange ? 'No pairings yet' : 'No data yet',
      subValue: summaryData.topDrinkPairing ?
                `+${formatPrice(summaryData.topDrinkPairing.additionalRevenue)}` : '',
      color: 'text-purple-400'
    },
    {
      icon: DollarSign,
      label: 'Sweet Spot',
      value: loading ? 'Loading...' :
             summaryData.bestPriceRange && summaryData.bestPriceRange.range ?
             `${summaryData.bestPriceRange.range}` :
             summaryData.dateRange ? 'No items yet' : 'No data yet',
      subValue: summaryData.bestPriceRange ?
                `${summaryData.bestPriceRange.percentage.toFixed(0)}% of orders` : '',
      color: 'text-yellow-400'
    }
  ];

  if (!hasAccess) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="relative overflow-hidden"
      >
        {/* Locked State */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gray-100 rounded-xl">
                <Lock className="h-6 w-6 text-gray-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-600">Customer Order Intelligence</h3>
                <p className="text-sm text-gray-400">Available for Delicious & Gourmet plans</p>
              </div>
            </div>
          </div>

          <p className="text-gray-500 mb-6">
            Unlock powerful insights about your customers' dining preferences and spending patterns to boost revenue.
          </p>

          <button
            onClick={() => navigate('/subscription')}
            className="w-full py-3 bg-[#FDB913] text-black rounded-lg font-bold hover:bg-[#e5a811] transition-all flex items-center justify-center gap-2"
          >
            Upgrade to Unlock
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
      className="relative overflow-hidden group cursor-pointer"
      onClick={() => navigate('/customer-insights')}
    >
      {/* Animated Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-100/50 via-blue-100/50 to-emerald-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl" />

      {/* Main Card */}
      <div className="relative bg-white rounded-xl p-6 border border-gray-200 shadow-sm group-hover:border-[#FDB913]/50 group-hover:shadow-md transition-all">
        {/* Header with Sparkle Effect */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <motion.div
              className="p-3 bg-[#FDB913]/20 rounded-xl relative"
              whileHover={{ scale: 1.05 }}
            >
              <Users className="h-6 w-6 text-[#FDB913]" />
              <motion.div
                className="absolute -top-1 -right-1"
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="h-4 w-4 text-[#FDB913]" />
              </motion.div>
            </motion.div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Customer Order Intelligence</h3>
              <p className="text-sm text-gray-500">
                {summaryData.dateRange ? (
                  <>
                    Last Refresh: {new Date(summaryData.dateRange.start + 'T12:00:00').toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })} - {new Date(summaryData.dateRange.end + 'T12:00:00').toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </>
                ) : (
                  'Weekly insights refreshed every Sunday morning'
                )}
              </p>
            </div>
          </div>
          <motion.div
            className="text-[#FDB913] opacity-0 group-hover:opacity-100 transition-opacity"
            initial={{ x: -10 }}
            whileHover={{ x: 0 }}
          >
            <ArrowRight className="w-6 h-6" />
          </motion.div>
        </div>

        {/* Insights Grid */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {insights.map((insight, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              className="bg-gray-50 rounded-lg p-4 border border-gray-100 group-hover:bg-gray-100 transition-all"
            >
              <insight.icon className={`h-5 w-5 ${insight.color === 'text-yellow-400' ? 'text-[#FDB913]' : insight.color === 'text-emerald-400' ? 'text-emerald-500' : insight.color === 'text-purple-400' ? 'text-purple-500' : insight.color} mb-2`} />
              <p className="text-gray-700 text-sm font-medium mb-1">{insight.label}</p>
              <p className={`text-xs ${insight.color === 'text-yellow-400' ? 'text-[#b8860b]' : insight.color === 'text-emerald-400' ? 'text-emerald-600' : insight.color === 'text-purple-400' ? 'text-purple-600' : insight.color} font-medium truncate`}>{insight.value}</p>
              {insight.subValue && (
                <p className="text-xs text-gray-500 mt-1">{insight.subValue}</p>
              )}
            </motion.div>
          ))}
        </div>

        {/* Weekly Data Preview */}
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg p-4 border border-emerald-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <p className="text-sm text-emerald-600">
                {loading ? 'Loading insights...' :
                 summaryData.dateRange && summaryData.weeklyOrders ?
                 `${summaryData.weeklyOrders} orders analyzed this week` :
                 'No orders yet - insights will appear after your first week'}
              </p>
            </div>
            <motion.div
              className="flex items-center gap-1"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <div className="w-1 h-3 bg-emerald-300 rounded-full" />
              <div className="w-1 h-5 bg-emerald-400 rounded-full" />
              <div className="w-1 h-4 bg-emerald-400 rounded-full" />
              <div className="w-1 h-6 bg-emerald-500 rounded-full" />
              <div className="w-1 h-3 bg-emerald-300 rounded-full" />
            </motion.div>
          </div>
        </div>

        {/* CTA */}
        <motion.div
          className="mt-6 text-center"
          whileHover={{ scale: 1.02 }}
        >
          <p className="text-gray-700 text-sm font-medium">
            {loading ? 'Loading intelligence...' :
             summaryData.topCombo && summaryData.topCombo.totalPrice > 0 ?
             `Customers spending ${formatPrice(summaryData.topCombo.totalPrice)} on winning combos →` :
             'Click to discover revenue-boosting patterns →'}
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default CustomerInsightsCard;